'use client'

/**
 * LoginForm — refactor TIER 1+2 paleta DS fast-food-loud.
 *
 * - Inputs DS con focus ring amarillo + border rojo
 * - Botón submit rojo con shadow brutalist
 * - Show password toggle (👁/🙈)
 * - Loading state con spinner SVG inline
 * - Error message rediseñado con icon + bg semantic
 * - "Recordar email" checkbox + localStorage
 * - Links secundarios DS (rojo + uppercase tracking-widest)
 */
import { enviarSinVaciar } from '@/shared/lib/enviarSinVaciar'
import { useRef, useState, useSyncExternalStore, useTransition } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { loginAction } from '@/features/auth/services/authActions'

const REMEMBER_EMAIL_KEY = 'login-remember-email'

/** localStorage no avisa cambios en la misma pestaña: no hay a qué suscribirse. */
function sinSuscripcion(): () => void {
  return () => {}
}

function leerEmailGuardado(): string | null {
  try {
    return localStorage.getItem(REMEMBER_EMAIL_KEY) || null
  } catch {
    return null
  }
}

function sinEmailEnServidor(): string | null {
  return null
}

export function LoginForm() {
  const [error, setError] = useState<string | null>(null)
  const [isPending, startTransition] = useTransition()
  const [showPassword, setShowPassword] = useState(false)
  // El email guardado se lee con useSyncExternalStore y no con un efecto que
  // haga setState: en el servidor no hay localStorage (se lee null) y React
  // pasa al valor guardado al hidratar, sin renders en cascada.
  const emailGuardado = useSyncExternalStore(sinSuscripcion, leerEmailGuardado, sinEmailEnServidor)
  const [emailEscrito, setEmail] = useState<string | null>(null)
  const [recordarElegido, setRememberEmail] = useState<boolean | null>(null)
  const email = emailEscrito ?? emailGuardado ?? ''
  const rememberEmail = recordarElegido ?? Boolean(emailGuardado)
  const errorRef = useRef<HTMLParagraphElement>(null)
  const searchParams = useSearchParams()
  // PRP-042: ?redirect= para volver a /accept-invite/[token] tras login
  const redirectTo = searchParams.get('redirect')

  function onSubmit(formData: FormData) {
    setError(null)
    const emailValue = String(formData.get('email') ?? '')
    const passwordValue = String(formData.get('password') ?? '')
    // Persistir email si checkbox activo (antes del submit por si redirige)
    try {
      if (rememberEmail) {
        localStorage.setItem(REMEMBER_EMAIL_KEY, emailValue)
      } else {
        localStorage.removeItem(REMEMBER_EMAIL_KEY)
      }
    } catch {
      /* ignore */
    }
    startTransition(async () => {
      const result = await loginAction({
        email: emailValue,
        password: passwordValue,
        redirectTo: redirectTo ?? undefined,
      })
      if (!result.ok) {
        setError(result.error)
        // Asegurar que el error sea visible en mobile (con teclado abierto puede quedar fuera)
        requestAnimationFrame(() => {
          errorRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
        })
      }
    })
  }

  return (
    <form onSubmit={(e) => enviarSinVaciar(e, onSubmit)} className="space-y-4">
      {/* Email */}
      <div className="space-y-1.5">
        <label
          htmlFor="email"
          className="text-xs font-black uppercase tracking-widest text-[#da291c] block"
        >
          Email
        </label>
        <input
          id="email"
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="tu@restaurante.com"
          className="w-full rounded-full bg-white border-2 border-[#1a1a1a]/15 px-4 py-2.5 min-h-[44px] text-base sm:text-sm font-bold tracking-tight placeholder:text-[#1a1a1a]/30 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#ffc72c]/50 focus-visible:border-[#da291c] hover:border-[#1a1a1a] transition-[border-color,box-shadow] duration-200 [touch-action:manipulation]"
        />
      </div>

      {/* Password con toggle */}
      <div className="space-y-1.5">
        <div className="flex items-baseline justify-between gap-2">
          <label
            htmlFor="password"
            className="text-xs font-black uppercase tracking-widest text-[#da291c] block"
          >
            Contraseña
          </label>
          <Link
            href="/forgot-password"
            className="text-xs font-black uppercase tracking-widest text-[#1a1a1a]/60 lg:text-[#1a1a1a]/50 hover:text-[#da291c] active:scale-95 active:text-[#da291c] py-1 -my-1 px-1 -mx-1 transition-[color,transform] duration-150 focus-visible:outline-none focus-visible:underline focus-visible:text-[#da291c] [touch-action:manipulation]"
          >
            ¿Olvidaste?
          </Link>
        </div>
        <div className="relative">
          <input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            required
            placeholder="••••••••"
            className="w-full rounded-full bg-white border-2 border-[#1a1a1a]/15 px-4 py-2.5 pr-14 min-h-[44px] text-base sm:text-sm font-bold tracking-tight placeholder:text-[#1a1a1a]/30 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#ffc72c]/50 focus-visible:border-[#da291c] hover:border-[#1a1a1a] transition-[border-color,box-shadow] duration-200 [touch-action:manipulation]"
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-11 h-11 sm:w-9 sm:h-9 rounded-full text-[#1a1a1a]/40 hover:text-[#da291c] hover:bg-[#fff8e7] active:scale-90 transition-[transform,background-color,color] duration-150 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#ffc72c]/50 [touch-action:manipulation]"
          >
            {showPassword ? (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden="true">
                <path fillRule="evenodd" d="M3.28 2.22a.75.75 0 00-1.06 1.06l14.5 14.5a.75.75 0 101.06-1.06l-1.745-1.745a10.029 10.029 0 003.3-4.38 1.651 1.651 0 000-1.185A10.004 10.004 0 009.999 3a9.956 9.956 0 00-4.744 1.194L3.28 2.22zM7.752 6.69l1.092 1.092a2.5 2.5 0 013.374 3.373l1.091 1.092a4 4 0 00-5.557-5.557z" clipRule="evenodd" />
                <path d="M10.748 13.93l2.523 2.523a9.987 9.987 0 01-3.27.547c-4.258 0-7.894-2.66-9.337-6.41a1.651 1.651 0 010-1.186A10.007 10.007 0 012.839 6.02L6.07 9.252a4 4 0 004.678 4.678z" />
              </svg>
            ) : (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden="true">
                <path d="M10 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" />
                <path fillRule="evenodd" d="M.664 10.59a1.651 1.651 0 010-1.186A10.004 10.004 0 0110 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0110 17c-4.257 0-7.893-2.66-9.336-6.41zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Recordar email */}
      <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-[#1a1a1a]/70 select-none min-h-[44px] sm:min-h-0 py-2 px-2 -mx-2 sm:py-0 sm:px-0 sm:mx-0 rounded-lg active:bg-[#da291c]/5 sm:active:bg-transparent transition-colors duration-150 [touch-action:manipulation]">
        <input
          type="checkbox"
          checked={rememberEmail}
          onChange={(e) => setRememberEmail(e.target.checked)}
          className="w-5 h-5 sm:w-4 sm:h-4 accent-[#da291c] cursor-pointer"
        />
        <span>Recordar email</span>
      </label>

      {/* Error message */}
      {error && (
        <p
          ref={errorRef}
          role="alert"
          aria-live="polite"
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-[#da291c] bg-[#da291c]/10 border-2 border-[#da291c]/30 rounded-[1rem] px-3 py-2 w-full animate-[slideInDown_200ms_ease-out]"
        >
          <span aria-hidden="true">⚠</span>
          {error}
        </p>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={isPending}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#da291c] text-white border-2 border-[#1a1a1a] px-4 py-3 min-h-[48px] text-sm font-black uppercase tracking-widest active:scale-95 hover:bg-[#1a1a1a] transition-[transform,background-color,box-shadow] duration-200 hover:shadow-[0_0_24px_-6px_#da291c] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#ffc72c]/50 [touch-action:manipulation] shadow-[3px_3px_0_#1a1a1a] disabled:bg-[#1a1a1a]/30 disabled:text-[#1a1a1a]/50 disabled:cursor-not-allowed disabled:shadow-none disabled:border-[#1a1a1a]/20"
      >
        {isPending ? (
          <>
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
              <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            </svg>
            Entrando...
          </>
        ) : (
          'Entrar a mi negocio'
        )}
      </button>

      {/* Divider + signup CTA */}
      <div className="pt-4 mt-3 sm:pt-2 sm:mt-2 border-t-2 border-dashed border-[#1a1a1a]/10 text-center">
        <p className="text-xs font-bold uppercase tracking-widest text-[#1a1a1a]/50">
          ¿No tienes cuenta?
        </p>
        <Link
          href="/signup"
          className="inline-flex items-center justify-center gap-1.5 mt-2 rounded-full bg-white text-[#da291c] border-2 border-[#da291c] px-4 py-2 min-h-[44px] sm:min-h-[40px] text-xs font-black uppercase tracking-widest hover:bg-[#da291c] hover:text-white active:scale-95 transition-[transform,background-color,color] duration-150 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#ffc72c]/50 [touch-action:manipulation]"
        >
          🎉 Prueba 1 mes gratis
        </Link>
      </div>
    </form>
  )
}
